import { HankoDialog } from "../../../../components/HankoDialog";
import { Alert, Box, FormControlLabel, FormGroup, Switch, TextField, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { FormButtons } from "../../../../components/form/FormButtons";
import { SnackbarContext } from "../../../../components/Snackbar/SnackbarProvider";
import { ApiClient } from "../../../../client/ApiClient";
import { Organization } from "../../../../models/Organization";
import { AttributeMap, Project } from "../../../../models/Project";
import { useRevalidator } from "react-router-dom";
import { IncludedQuantity, Price, Subscription } from "../../../../models/Subscription";
import { isStandardAttributeMapEmpty, samlAttributeMapHelperText, samlStandardAttributeFields, StandardAttributeMapFields } from "../../../../components/form/StandardAttributeMapFields";
import { ClaimMappingEditor } from "../../../../components/form/ClaimMappingEditor";

interface Props {
  open: boolean
  onClose: () => void
  organization: Organization
  project: Project
  subscription: Subscription | null
}

interface State {
  name: string
  domain: string
  metadata_url: string
  skip_email_verification: boolean
  attribute_map: Record<string, string>
  custom_claim_mapping: Record<string, string>
}

const emptyState: State = {
  name: '',
  domain: '',
  metadata_url: '',
  skip_email_verification: false,
  attribute_map: {},
  custom_claim_mapping: {}
}

export const CreateEnterpriseConnectionModal = (props: Props) => {
  const { open, onClose, organization, project, subscription } = props
  const [loading, setLoading] = useState(false)
  const [state, setState] = useState<State>(emptyState)
  const { error, success } = useContext(SnackbarContext)
  const revalidator = useRevalidator()
  const [price, setPrice] = useState<Price>()
  const [includedQuantity, setIncludedQuantity] = useState<IncludedQuantity>()

  useEffect(() => {
    if (subscription !== null && subscription !== undefined) {
      for (const includedQuantity of subscription.included_quantities) {
        if (includedQuantity.type === "enterprise_connections") {
          setIncludedQuantity(includedQuantity)
          break
        }
      }

      for (const priceItem of subscription.prices) {
        if (priceItem.feature === "enterprise_connections") {
          setPrice(priceItem)
          break
        }
      }
    }

  }, [subscription, open])

  const onSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const isStandardEmpty = isStandardAttributeMapEmpty(samlStandardAttributeFields, state.attribute_map)
      const isCustomEmpty = Object.keys(state.custom_claim_mapping).length === 0
      const attribute_map: AttributeMap | undefined = isStandardEmpty && isCustomEmpty ? undefined : {
        ...state.attribute_map,
        custom: isCustomEmpty ? undefined : state.custom_claim_mapping,
      }

      await ApiClient.createSamlIdentityProvider(organization.id, project.id, {
        enabled: true,
        name: state.name,
        domain: state.domain,
        metadata_url: state.metadata_url,
        skip_email_verification: state.skip_email_verification,
        attribute_map
      })
      revalidator.revalidate()
      success("Created new connection successfully")
      onCancel()
    } catch (e) {
      if (e instanceof Response && e.status === 409) {
        error("Connection already exists")
      } else if (e instanceof Response && e.status === 400) {
        const errorResponse = await e.json()
        error(errorResponse.message ?? "Failed to create new connection")
      } else {
        error("Failed to create new connection")
      }
    }
    setLoading(false)
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    })
  }

  const onChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [e.target.name]: e.target.checked
    })
  }

  const onCancel = () => {
    setState(emptyState)
    onClose()
  }

  return <HankoDialog onClose={onCancel} open={open} fullScreen>
    <Box sx={{ margin: 'auto', maxWidth: '500px', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4">New SAML connection</Typography>
      <Typography sx={{ mt: 2.5, mb: 3 }}>Create a new SAML connection below.</Typography>
      {subscription !== undefined && (subscription?.is_active ?? false) && (project.saml.identity_providers?.length ?? 0) >= (includedQuantity?.quantity ?? 0) ?
        <Alert variant="outlined" severity="warning" sx={{ mb: 3 }}>Enterprise connections are a paid feature. The free
          limit ({includedQuantity?.quantity}) will be exceeded and your organization will be
          charged <b>{new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: price?.currency ?? "USD"
          }).format((price?.amount ?? 0) / 100,)} per month </b> for this connection.</Alert> : null
      }
      <form onSubmit={onSubmit}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField name="name" label="Name" fullWidth type="text" onChange={onChange} value={state.name}
                     required autoComplete="off" />
          <TextField name="domain" label="Domain" fullWidth type="text" onChange={onChange} value={state.domain}
                     required autoComplete="off" />
          <TextField name="metadata_url" label="Metadata URL" fullWidth type="url" onChange={onChange}
                     value={state.metadata_url} required autoComplete="off" />
          <FormGroup>
            <FormControlLabel name="skip_email_verification"
                              control={<Switch name="skip_email_verification" onChange={onChecked}
                                               checked={state.skip_email_verification} color="success" />}
                              label="Skip email verification (if the SAML IdP does not specify email_verified)" />
          </FormGroup>
          <StandardAttributeMapFields fields={samlStandardAttributeFields} helperText={samlAttributeMapHelperText}
                                      value={state.attribute_map}
                                      onChange={(attribute_map) => setState({ ...state, attribute_map })} />
          <ClaimMappingEditor label="Custom claims" valueLabel="SAML attribute name"
                              helperText="Map this connection's SAML attributes onto your tenant's declared custom claims."
                              value={state.custom_claim_mapping}
                              onChange={(custom_claim_mapping) => setState({ ...state, custom_claim_mapping })}
                              knownClaimNames={Object.keys(project.custom_claims ?? {})} />
        </Box>
        <FormButtons onCancel={onCancel} loading={loading} disabled={false} sx={{ mt: 5 }} />
      </form>
    </Box>
  </HankoDialog>
}